import { AlertTriangle } from 'lucide-react';

interface NoTasksNoticeProps {
  /**
   * True when the job has at least one live task. `undefined` means "not known
   * yet" (payload still loading, or an older response without the field) and is
   * deliberately not treated as a fault.
   */
  hasTasks: boolean | undefined;
  /** True when a live schedule row exists for the job. */
  isScheduled: boolean | undefined;
}

/**
 * Warning banner shown when a scheduled job has no tasks — the state in which the
 * job is invisible on every engineer's mobile device.
 *
 * Scheduling creates one job_planned_task row per task × team member
 * (plannerService.createEvent) and inserts nothing when the task list is empty;
 * the mobile "my jobs" query INNER JOINs job_planned_task, so the job silently
 * fails to appear. Adding a task back-fills the planned rows for every active
 * plan (addJobTask, WRK-5738), so this notice clears once tasks are added —
 * which is why it points at the fix rather than just reporting the fault.
 *
 * Gated on the job being scheduled, matching NoTasksBadge and
 * NoTasksWarningIcon: an unscheduled task-less job is not yet expected on mobile
 * (WRK-5792).
 */
export function NoTasksNotice({ hasTasks, isScheduled }: NoTasksNoticeProps) {
  if (!isScheduled) return null;
  if (hasTasks !== false) return null;

  return (
    <div
      role="alert"
      className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
    >
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>
        This job won't show up on mobile until it has at least one task. Add a task and it will
        appear for everyone assigned to it.
      </span>
    </div>
  );
}
